// QuickInsightsModal.tsx — Sprint 14. Read-only insights pane with
// Financial Overview / Collection Performance / Occupancy Status /
// Recommendations cards. All data flows in via props.
import React from 'react';
import { Card, Col, List, Modal, Progress, Row, Space, Statistic, Typography } from 'antd';
import {
  ArrowDownOutlined, BarChartOutlined, InfoCircleOutlined,
} from '@ant-design/icons';

const { Text } = Typography;

export interface QuickInsightsModalProps {
  open: boolean;
  onCancel: () => void;
  feeCollectionRate: number;
  occupancyRate:     number;
  totalOwners:       number;
}

export const QuickInsightsModal: React.FC<QuickInsightsModalProps> = ({
  open, onCancel, feeCollectionRate, occupancyRate, totalOwners,
}) => {
  const occupied = Math.round(totalOwners * (occupancyRate / 100));
  const vacant   = totalOwners - occupied;

  return (
    <Modal
      title={<Space><BarChartOutlined style={{ color: '#4299e1' }} /><span>Quick Insights</span></Space>}
      open={open}
      onCancel={onCancel}
      footer={null}
      width={900}
      centered
    >
      <Row gutter={[24, 24]}>
        <Col span={24}>
          <Card title="Financial Overview" size="small">
            <Row gutter={24}>
              <Col span={8}>
                <Statistic
                  title="Revenue Growth" value={12.5} suffix="%"
                  valueStyle={{ color: '#48bb78' }}
                  prefix={<ArrowDownOutlined style={{ transform: 'rotate(45deg)' }} />}
                />
              </Col>
              <Col span={8}>
                <Statistic title="Expense Ratio" value={68.3} suffix="%" valueStyle={{ color: '#ed8936' }} />
              </Col>
              <Col span={8}>
                <Statistic title="Profit Margin" value={31.7} suffix="%" valueStyle={{ color: '#4299e1' }} />
              </Col>
            </Row>
          </Card>
        </Col>

        <Col span={12}>
          <Card title="Collection Performance" size="small">
            <Space direction="vertical" style={{ width: '100%' }}>
              <div>
                <Text>Current Month: {feeCollectionRate}%</Text>
                <Progress percent={feeCollectionRate} strokeColor={{ '0%': '#4299e1', '100%': '#48bb78' }} />
              </div>
              <div>
                <Text>Target: 95%</Text>
                <Progress percent={95} status="normal" strokeColor="#cbd5e0" />
              </div>
            </Space>
          </Card>
        </Col>

        <Col span={12}>
          <Card title="Occupancy Status" size="small">
            <Space direction="vertical" style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text>Occupied Units</Text><Text strong>{occupied}</Text>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text>Vacant Units</Text><Text strong>{vacant}</Text>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text>Occupancy Rate</Text><Text strong>{occupancyRate}%</Text>
              </div>
            </Space>
          </Card>
        </Col>

        <Col span={24}>
          <Card title="Recommendations" size="small">
            <List
              size="small"
              dataSource={[
                'Increase collection efforts for overdue accounts',
                'Consider raising fees by 3% for next quarter',
                'Schedule maintenance for common areas',
                'Send renewal notices to expiring leases',
              ]}
              renderItem={(item) => (
                <List.Item>
                  <List.Item.Meta
                    avatar={<InfoCircleOutlined style={{ color: '#4299e1' }} />}
                    description={item}
                  />
                </List.Item>
              )}
            />
          </Card>
        </Col>
      </Row>
    </Modal>
  );
};
